import React from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'

import ChartTooltip from './ChartTooltip'
import Pager from './Pager'
import usePagination, { DEFAULT_PAGE_SIZE } from './usePagination'

// Each row needs vertical room for two stacked bars, plus the x-axis band.
const ROW_HEIGHT = 34
const AXIS_BAND = 80
const MIN_HEIGHT = 260

/**
 * Who is doing how much, and of what.
 *
 * Two bars per user -- created and updated -- each stacked by entity. Colour
 * carries the entity (`series` comes from `entityColorFor` in
 * reportRegistry.js, one palette slot per distinct entity in the report, so
 * every one gets a permanent slot and never shares a hue with another),
 * and position carries create-vs-update. That keeps a colour meaning the
 * same thing across every chart on this tab, and avoids the trap of
 * colouring by action: a report can have more actions than the palette has
 * slots (`incidents` has ten, across only five entities), and cycling the
 * palette to cover them would defeat colour-vision accessibility.
 *
 * Horizontal, because user names are long and reading them rotated is miserable.
 * Paged, because two bars per person adds up fast -- page one is the top N by
 * total effort.
 */
const Leaderboard = ({ data, series, pageSize = DEFAULT_PAGE_SIZE }) => {
    const { pageItems, page, pageCount, setPage, start, total } = usePagination(data, pageSize)
    const height = Math.max(MIN_HEIGHT, pageItems.length * ROW_HEIGHT + AXIS_BAND)

    return (
        <>
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={pageItems} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }} barGap={2}>
                    <CartesianGrid horizontal={false} stroke="#e1e0d9" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} stroke="#c3c2b7" />
                    <YAxis
                        type="category"
                        dataKey="userName"
                        width={150}
                        tick={{ fontSize: 11, fill: '#52514e' }}
                        stroke="#c3c2b7"
                        interval={0}
                    />
                    <Tooltip content={<ChartTooltip total />} cursor={{ fill: 'rgba(11,11,11,0.04)' }} />
                    {/* One legend entry per entity: the created/updated split is carried by
                        bar position, so listing all fourteen series would be noise. */}
                    <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        payload={series.map((entity) => ({ value: entity.label, type: 'square', color: entity.color, id: entity.key }))}
                    />
                    {series.map((entity) => (
                        <Bar
                            key={`created-${entity.key}`}
                            dataKey={`created_${entity.key}`}
                            name={`${entity.label} · created`}
                            stackId="created"
                            fill={entity.color}
                            stroke="#ffffff"
                            strokeWidth={2}
                            radius={[0, 2, 2, 0]}
                        />
                    ))}
                    {series.map((entity) => (
                        <Bar
                            key={`updated-${entity.key}`}
                            dataKey={`updated_${entity.key}`}
                            name={`${entity.label} · updated`}
                            stackId="updated"
                            fill={entity.color}
                            stroke="#ffffff"
                            strokeWidth={2}
                            radius={[0, 2, 2, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <Pager
                page={page}
                pageCount={pageCount}
                setPage={setPage}
                start={start}
                count={pageItems.length}
                total={total}
                unit="users"
            />
        </>
    )
}

export default Leaderboard
